/**
 * DateRangePicker Component
 * Date range selector with preset buttons (7d/30d/90d/custom)
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Calendar } from 'lucide-react';
import { getDateRangeFromPreset } from '@/hooks/useAnalytics';
import type { DateRangePreset } from '@/types/analytics';

export interface DateRange {
  startDate: string; // ISO 8601
  endDate: string; // ISO 8601
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
}

const PRESETS: Array<{ label: string; value: DateRangePreset }> = [
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
  { label: 'Custom', value: 'custom' },
];

export function DateRangePicker({ value, onChange, className = '' }: DateRangePickerProps) {
  const [activePreset, setActivePreset] = useState<DateRangePreset>('30d');
  const [showCustom, setShowCustom] = useState(false);

  const handlePresetClick = (preset: DateRangePreset) => {
    setActivePreset(preset);

    if (preset === 'custom') {
      setShowCustom(true);
    } else {
      setShowCustom(false);
      const dateRange = getDateRangeFromPreset(preset);
      onChange(dateRange);
    }
  };

  const handleCustomDateChange = (field: 'start' | 'end', dateString: string) => {
    if (!dateString) return;

    const date = new Date(dateString);
    if (field === 'start') {
      date.setHours(0, 0, 0, 0);
      onChange({
        startDate: date.toISOString(),
        endDate: value.endDate,
      });
    } else {
      date.setHours(23, 59, 59, 999);
      onChange({
        startDate: value.startDate,
        endDate: date.toISOString(),
      });
    }
  };

  const formatDateForInput = (isoDate: string): string => {
    const date = new Date(isoDate);
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
  };

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      {/* Preset buttons */}
      <div className="flex gap-2">
        {PRESETS.map((preset) => (
          <Button
            key={preset.value}
            variant={activePreset === preset.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => handlePresetClick(preset.value)}
            className="flex items-center gap-2"
          >
            {preset.value === 'custom' && <Calendar className="h-4 w-4" />}
            {preset.label}
          </Button>
        ))}
      </div>

      {/* Custom date inputs */}
      {showCustom && (
        <Card className="p-4 border border-border">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={formatDateForInput(value.startDate)}
                onChange={(e) => handleCustomDateChange('start', e.target.value)}
                max={formatDateForInput(value.endDate)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={formatDateForInput(value.endDate)}
                onChange={(e) => handleCustomDateChange('end', e.target.value)}
                min={formatDateForInput(value.startDate)}
                max={formatDateForInput(new Date().toISOString())}
              />
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
